import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED = ["en", "fr", "pt"];
const DEFAULT = "en";

const LanguageContext = createContext({
  lang: DEFAULT,
  setLang: () => {},
  prefix: (p) => p,
  swapPathLang: () => "/",
});

/**
 * Split a pathname into ["fr" | "", rest]. Always returns rest with a
 * leading "/".
 */
function splitLangFromPath(pathname) {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const parts = clean.split("/").filter(Boolean);
  if (parts.length > 0 && SUPPORTED.includes(parts[0]) && parts[0] !== DEFAULT) {
    return { lang: parts[0], rest: "/" + parts.slice(1).join("/") };
  }
  return { lang: DEFAULT, rest: clean };
}

/** Wrap a path in the current language prefix, preserving query + hash. */
function withPrefix(to, lang) {
  if (typeof to !== "string") return to;
  if (/^(https?:)?\/\//.test(to) || to.startsWith("mailto:") || to.startsWith("tel:")) {
    return to;
  }
  // Normalise: ensure leading slash
  const t = to.startsWith("/") ? to : `/${to}`;
  if (lang === DEFAULT) return t;
  // Strip any existing lang segment to avoid /fr/fr/…
  const { rest } = splitLangFromPath(t);
  return `/${lang}${rest === "/" ? "" : rest}`;
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const { lang, rest } = splitLangFromPath(location.pathname);

  // Sync i18n whenever the URL segment changes.
  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = lang;
    try {
      localStorage.setItem("ix.lang", lang);
    } catch (_) { /* ignore */ }
  }, [lang, i18n]);

  // First-visit geo detection has been moved to <LanguageDetectedBanner> —
  // it now shows a dismissible top bar instead of silently redirecting.
  // (No-op here.)

  const value = useMemo(() => {
    return {
      lang,
      setLang: (next) => {
        if (!SUPPORTED.includes(next) || next === lang) return;
        const target =
          next === DEFAULT
            ? rest || "/"
            : `/${next}${rest === "/" ? "" : rest}`;
        navigate(`${target}${location.search}${location.hash}`, { replace: false });
      },
      prefix: (p) => withPrefix(p, lang),
      swapPathLang: (nextLang) =>
        nextLang === DEFAULT
          ? rest || "/"
          : `/${nextLang}${rest === "/" ? "" : rest}`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, rest, location.search, location.hash]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}

/**
 * <LocalizedLink> — behaves like react-router's Link but automatically
 * prepends the active language prefix to internal `to` paths.
 */
export function LocalizedLink({ to, ...rest }) {
  const { prefix } = useLang();
  return <Link to={prefix(to)} {...rest} />;
}

export { SUPPORTED as SUPPORTED_LANGS, DEFAULT as DEFAULT_LANG };
